import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import messagesEN from '#components/translations/en-GB.json';
import type { RootState } from '#store/store.ts';

import { DEFAULT_LOCALE, getSupportedLocale } from './lang';
import { type DisplayMessages, type ImportedMessages, normalizeDisplayMessages } from './messages.ts';

type CombinedMessages = Record<string, DisplayMessages>;

export type LangState = {
    allMessages: CombinedMessages;
    displayMessages: DisplayMessages | undefined;
    displayLocale: string | undefined;
};

type MessagesPayload = {
    locale: string;
    displayMessages: ImportedMessages;
};

const defaultMessages = {
    [DEFAULT_LOCALE]: normalizeDisplayMessages(messagesEN),
};

const initialState: LangState = {
    allMessages: defaultMessages,
    displayMessages: undefined,
    displayLocale: undefined,
};

const langSlice = createSlice({
    name: 'lang',
    initialState,
    reducers: {
        localeChanged: (state, action: PayloadAction<string>) => {
            const preferredLocale = action.payload;
            const displayLocale = getSupportedLocale(preferredLocale);

            state.displayLocale = displayLocale;
            state.displayMessages = state.allMessages[displayLocale];
        },
        displayMessagesFetched: (state, action: PayloadAction<MessagesPayload>) => {
            const { locale, displayMessages } = action.payload;
            const normalizedDisplayMessages = normalizeDisplayMessages(displayMessages);

            state.allMessages = {
                ...state.allMessages,
                [locale]: normalizedDisplayMessages,
            };
            state.displayMessages = normalizedDisplayMessages;
            state.displayLocale = locale;
        },
    },
});

export const { localeChanged, displayMessagesFetched } = langSlice.actions;

export const getLocale = (state: RootState) => state.lang.displayLocale;
export const getDisplayMessages = (state: RootState) => state.lang.displayMessages;

export default langSlice.reducer;
